import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Divider
} from '@material-ui/core';
import { Close, Storefront } from '@material-ui/icons';
import Rating from '@material-ui/lab/Rating';
import Alert from '@material-ui/lab/Alert';
import { http, AlertService, localstorage } from '../../fx-core';
import './ReviewDialog.scss';

interface ReviewDialogProps {
  open: boolean;
  onClose: () => void;
  order: any;
  onReviewSubmitted: () => void;
  onViewYourReviewClick: () => void;
}

const ReviewDialog: React.FC<ReviewDialogProps> = ({ 
  open, 
  onClose, 
  order, 
  onReviewSubmitted, 
  onViewYourReviewClick
}) => {
  const [rating, setRating] = useState<number>(5);
  const [hoverRating, setHoverRating] = useState<number>(-1);
  const [reviewTitle, setReviewTitle] = useState<string>('');
  const [reviewText, setReviewText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [canReview, setCanReview] = useState<boolean>(true);
  const [existingReviewId, setExistingReviewId] = useState<number | null>(null);
  const [checkingEligibility, setCheckingEligibility] = useState<boolean>(true);
  const [eligibilityMessage, setEligibilityMessage] = useState<string>('');

  const userId = Number(localstorage.getItem('userId'));

  useEffect(() => {
    if (open && order) {
      checkReviewEligibility();
    }
  }, [open, order]);

  const checkReviewEligibility = async () => {
    setCheckingEligibility(true);
    try {
      const apiOptions: any = {
        url: `stallreviews/canreview/${order.id}?userId=${userId}`
      };
      const response = await http.get(apiOptions);

      if (response.data.success) {
        setCanReview(response.data.canReview);
        if (!response.data.canReview) {
          setEligibilityMessage(response.data.reason);
          if (response.data.reviewId) {
            setExistingReviewId(response.data.reviewId);
          }
        }
      }
    } catch (error) {
      console.error('Error checking review eligibility:', error);
      setCanReview(true);
      AlertService.showMsg({ 
        message: 'Could not verify review status. You may proceed with your review.' 
      });
    } finally {
      setCheckingEligibility(false);
    }
  };

  const handleSubmit = async () => {
    if (!reviewText.trim()) {
      AlertService.showMsg({ message: 'Please write a review' });
      return;
    }

    setLoading(true);
    try {
      const reviewData = {
        orderId: order.id,
        userId: userId,
        rating: rating,
        title: reviewTitle.trim(),
        description: reviewText.trim()
      };

      const apiOptions: any = {
        url: 'stallreviews',
        data: reviewData
      };

      const response = await http.post(apiOptions);

      if (response.data.success) {
        AlertService.showSuccessMsg('Review submitted successfully! Thank you for your feedback.');
        onReviewSubmitted();
        handleClose();
      } else {
        AlertService.showMsg({ message: response.data.error || 'Failed to submit review' });
      }
    } catch (error: any) {
      console.error('Error submitting review:', error);
      const errorMsg = error?.response?.data?.error || 'Failed to submit review. Please try again.';
      AlertService.showMsg({ message: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setRating(5);
    setHoverRating(-1);
    setReviewTitle('');
    setReviewText('');
    setCanReview(true);
    setExistingReviewId(null);
    setEligibilityMessage('');
    onClose();
  };

  const getRatingLabel = (value: number) => {
    if (value <= 1) return 'Poor';
    if (value <= 2) return 'Fair';
    if (value <= 3) return 'Good';
    if (value <= 4) return 'Very Good';
    return 'Excellent';
  };

  // Custom logic to prevent .5 rating for the first star: minimum is 1
  const handleRatingChange = (event: React.ChangeEvent<{}>, newValue: number | null) => {
    // Only allow .5 steps except for first star, which must be 1
    if (newValue !== null) {
      let correctedValue = newValue;
      if (newValue < 1) {
        correctedValue = 1;
      } else if (newValue > 1 && (newValue * 2) % 1 !== 0) {
        correctedValue = Math.round(newValue * 2) / 2;
      }
      setRating(correctedValue);
    }
  };

  // Custom logic: do not show .5 on hovering for the first star
  const handleHoverChange = (event: React.ChangeEvent<{}>, newHover: number) => {
    if (newHover < 1 && newHover !== -1) {
      setHoverRating(1);
    } else {
      setHoverRating(newHover);
    }
  };

  const displayRating = hoverRating !== -1 ? hoverRating : rating;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      className="review-dialog"
    >
      <DialogTitle className="review-dialog-title">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <Storefront className="stall-icon mr-2" />
            <Typography variant="h6">Rate & Review</Typography>
          </Box>
          <IconButton size="small" onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent className="review-dialog-content">
        {checkingEligibility ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        ) : !canReview ? (
          <Box py={2}>
            <Alert severity="warning">
              {eligibilityMessage === 'Already reviewed' ? (
                <>
                  You have already reviewed this order.{` `}
                  <a 
                    href="#" 
                    onClick={(e) => { 
                      e.preventDefault(); 
                      handleClose();
                      onViewYourReviewClick();
                    }}
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    View your review
                  </a>
                </>
              ) : (
                eligibilityMessage || 'This order cannot be reviewed at this time.'
              )}
            </Alert>
          </Box>
        ) : (
          <>
            <Box className="order-info-box" mb={3}>
              <Typography variant="subtitle2" color="textSecondary">
                Order #{order?.orderIdentifier}
              </Typography>
              <Typography variant="body1" className="stall-name">
                {order?.stallName}
              </Typography>
              <Chip
                label={order?.orderStatus}
                size="small"
                className="status-chip"
                color="primary"
              />
            </Box>

            <Box className="rating-section" mb={3}>
              <Typography variant="subtitle1" className="mb-2">
                How would you rate your experience?
              </Typography>
              <Box display="flex" flexDirection="column" alignItems="center" className="star-rating-container">
                <Rating
                  name="order-rating"
                  value={rating}
                  precision={0.5}
                  size="large"
                  onChange={handleRatingChange}
                  onChangeActive={handleHoverChange}
                  className="rating-stars"
                  // Minimum value is always 1, no half star for the first star
                  min={1}
                />
                <Box className="rating-info" mt={2}>
                  <Typography variant="h6" className="rating-label">
                    {getRatingLabel(displayRating)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {displayRating.toFixed(1)} out of 5 stars
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box mb={3}>
              <Typography variant="subtitle1" className="mb-2">
                Add a title (optional)
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="What's most important to know?"
                value={reviewTitle}
                onChange={(e) => setReviewTitle(e.target.value)}
                inputProps={{ maxLength: 200 }}
                helperText={`${reviewTitle.length}/200 characters`}
              />
            </Box>

            <Box mb={2}>
              <Typography variant="subtitle1" className="mb-2">
                Write your review *
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={5}
                variant="outlined"
                placeholder="Share your experience with others... What did you like or dislike about your order?"
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                inputProps={{ maxLength: 2000 }}
                helperText={`${reviewText.length}/2000 characters`}
                error={reviewText.length === 0}
              />
            </Box>

            <Box className="tips-box">
              <Typography variant="caption" color="textSecondary">
                <strong>Tips:</strong>Help others by sharing an honest experience — quality, delivery/service, communication, packaging, or value for money
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      {!checkingEligibility && canReview && (
        <>
          <Divider />
          <DialogActions className="review-dialog-actions">
            <Button onClick={handleClose} color="default">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="submit-review-btn"
              variant="contained"
              disabled={loading || !reviewText.trim()}
              startIcon={loading && <CircularProgress size={20} />}
            >
              {loading ? 'Submitting...' : 'Submit Review'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default ReviewDialog;
